import React, { useState } from 'react';
import { RightPanel } from './RightPanel';
import { User, Bot, Clock, DollarSign, FileCode, Database, Wrench, Puzzle, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { Interaction } from '../types';
import { AppCard } from './base/AppCard';
import { AppBadge } from './base/AppBadge';
interface HistoryDetailPanelProps {
  interaction: Interaction | null;
  isOpen: boolean;
  onClose: () => void;
}
export function HistoryDetailPanel({
  interaction,
  isOpen,
  onClose
}: HistoryDetailPanelProps) {
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  if (!interaction) return null;
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <RightPanel isOpen={isOpen} onClose={onClose} title="Dettaglio conversazione" subtitle={`${formatTimestamp(interaction.timestamp)} • ${interaction.userLabel}`} width="xl" data-id="element-204">
      <div className="p-6 space-y-6" data-id="element-205">
        {/* Header Info */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-dark-subtle" data-id="element-206">
          <div className="flex items-center gap-4" data-id="element-207">
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400" data-id="element-208">
              <User className="w-4 h-4 mr-2" data-id="element-209" />
              {interaction.userLabel}
            </div>
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400" data-id="element-210">
              <Clock className="w-4 h-4 mr-2" data-id="element-211" />
              {new Date(interaction.timestamp).toLocaleTimeString('it-IT', {
              hour: '2-digit',
              minute: '2-digit'
            })}
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-212">
            <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" data-id="element-213" />
            <span className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-214">
              €{(interaction.costEstimate / 100).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Input & Output Section */}
        <div data-id="element-215">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center" data-id="element-216">
            <Bot className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" data-id="element-217" />
            Conversazione
          </h3>

          <div className="space-y-4" data-id="element-218">
            {/* User Input */}
            <div className="flex gap-3" data-id="element-219">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-dark-medium flex items-center justify-center" data-id="element-220">
                <User className="w-4 h-4 text-gray-600 dark:text-gray-400" data-id="element-221" />
              </div>
              <div className="flex-1" data-id="element-222">
                <div className="bg-gray-50 dark:bg-dark-subtle rounded-2xl rounded-tl-none px-4 py-3 border border-gray-100 dark:border-dark-medium" data-id="element-223">
                  <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed" data-id="element-224">
                    {interaction.userInput}
                  </p>
                </div>
              </div>
            </div>

            {/* Assistant Output */}
            <div className="flex gap-3" data-id="element-225">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center" data-id="element-226">
                <Bot className="w-4 h-4 text-blue-600 dark:text-blue-400" data-id="element-227" />
              </div>
              <div className="flex-1" data-id="element-228">
                <div className="bg-blue-50 dark:bg-blue-900/10 rounded-2xl rounded-tl-none px-4 py-3 border border-blue-100 dark:border-blue-900/30" data-id="element-229">
                  <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed whitespace-pre-wrap" data-id="element-230">
                    {interaction.finalTextOutput}
                  </p>
                </div>
              </div>
            </div>

            {/* Widgets Output */}
            {interaction.finalWidgetsOutput && interaction.finalWidgetsOutput.length > 0 && <div className="ml-11 space-y-3" data-id="element-231">
                  {interaction.finalWidgetsOutput.map((widget, idx) => <div key={idx} className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-xl p-4" data-id="element-232">
                      <div className="flex items-center gap-2 mb-2" data-id="element-233">
                        <Puzzle className="w-4 h-4 text-purple-600 dark:text-purple-400" data-id="element-234" />
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-235">
                          Widget: {widget.type}
                        </span>
                      </div>
                      <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto" data-id="element-236">
                        {JSON.stringify(widget.payload, null, 2)}
                      </pre>
                    </div>)}
                </div>}
          </div>
        </div>

        {/* Prompt Used Section */}
        <div data-id="element-237">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center" data-id="element-238">
            <FileCode className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" data-id="element-239" />
            Prompt utilizzato
          </h3>

          <AppCard className="bg-purple-50/30 dark:bg-purple-900/10" data-id="element-240">
            <div className="space-y-3" data-id="element-241">
              <div className="flex flex-wrap gap-2" data-id="element-242">
                {interaction.promptSectionsUsed.map((section, idx) => <AppBadge key={idx} variant="purple" data-id="element-243">
                    {section}
                  </AppBadge>)}
              </div>

              <button onClick={() => setShowFullPrompt(!showFullPrompt)} className="flex items-center gap-2 text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors" data-id="element-244">
                {showFullPrompt ? <>
                    <ChevronUp className="w-4 h-4" data-id="element-245" />
                    Nascondi prompt completo
                  </> : <>
                    <ChevronDown className="w-4 h-4" data-id="element-246" />
                    Mostra prompt completo
                  </>}
              </button>

              {showFullPrompt && <div className="mt-4 p-4 bg-white dark:bg-dark-surface border border-purple-200 dark:border-purple-900/30 rounded-xl" data-id="element-247">
                  <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono" data-id="element-248">
                    {`# PREMESSA\n${interaction.promptSectionsUsed[0] || 'N/A'}\n\n# LOGICA\n${interaction.promptSectionsUsed[1] || 'N/A'}\n\n# OUTPUT\n${interaction.promptSectionsUsed[2] || 'N/A'}`}
                  </pre>
                </div>}
            </div>
          </AppCard>
        </div>

        {/* Knowledge Retrieved Section */}
        <div data-id="element-249">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center" data-id="element-250">
            <Database className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" data-id="element-251" />
            Conoscenza recuperata
          </h3>

          <div className="space-y-3" data-id="element-252">
            {interaction.traceSteps.filter(step => step.label.includes('conoscenza') || step.label.includes('Recupero')).map(step => <AppCard key={step.id} className="bg-green-50/30 dark:bg-green-900/10" data-id="element-253">
                  <div className="flex items-start gap-3" data-id="element-254">
                    <Database className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" data-id="element-255" />
                    <div className="flex-1 min-w-0" data-id="element-256">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1" data-id="element-257">
                        {step.label}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-258">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </AppCard>)}

            {interaction.traceSteps.filter(step => step.label.includes('conoscenza') || step.label.includes('Recupero')).length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400 italic" data-id="element-259">
                Nessuna fonte di conoscenza utilizzata
              </p>}
          </div>
        </div>

        {/* Tools Used Section */}
        <div data-id="element-260">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center" data-id="element-261">
            <Wrench className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" data-id="element-262" />
            Strumenti utilizzati
          </h3>

          <div className="grid grid-cols-1 gap-3" data-id="element-263">
            {interaction.toolsUsed.length > 0 ? interaction.toolsUsed.map((tool, idx) => {
            const toolStep = interaction.traceSteps.find(step => step.description.toLowerCase().includes(tool.toLowerCase()));
            return <AppCard key={idx} className="bg-blue-50/30 dark:bg-blue-900/10" data-id="element-264">
                    <div className="flex items-start gap-3" data-id="element-265">
                      <Wrench className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" data-id="element-266" />
                      <div className="flex-1 min-w-0" data-id="element-267">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1" data-id="element-268">
                          {tool}
                        </p>
                        {toolStep && <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-269">
                            {toolStep.description}
                          </p>}
                      </div>
                    </div>
                  </AppCard>;
          }) : <p className="text-sm text-gray-500 dark:text-gray-400 italic" data-id="element-270">
                Nessuno strumento utilizzato
              </p>}
          </div>
        </div>

        {/* Widgets Used Section */}
        <div data-id="element-271">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center" data-id="element-272">
            <Puzzle className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" data-id="element-273" />
            Widget utilizzati
          </h3>

          <div className="flex flex-wrap gap-2" data-id="element-274">
            {interaction.widgetsUsed.length > 0 ? interaction.widgetsUsed.map((widget, idx) => <AppBadge key={idx} variant="purple" icon={<Puzzle className="w-3 h-3" data-id="element-276" />} data-id="element-275">
                  {widget}
                </AppBadge>) : <p className="text-sm text-gray-500 dark:text-gray-400 italic" data-id="element-277">
                Nessun widget utilizzato
              </p>}
          </div>
        </div>

        {/* Trace Steps / Metadata Section */}
        <div data-id="element-278">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center" data-id="element-279">
            <Info className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400" data-id="element-280" />
            Trace esecuzione
          </h3>

          <div className="relative" data-id="element-281">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200 dark:bg-dark-medium" data-id="element-282" />

            <div className="space-y-4" data-id="element-283">
              {interaction.traceSteps.map((step, idx) => <div key={step.id} className="relative flex gap-4" data-id="element-284">
                  {/* Timeline dot */}
                  <div className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-white dark:bg-dark-surface border-2 border-blue-500 dark:border-blue-400 flex items-center justify-center" data-id="element-285">
                    <div className="w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400" data-id="element-286" />
                  </div>

                  {/* Step content */}
                  <div className="flex-1 pb-4" data-id="element-287">
                    <div className="flex items-center gap-2 mb-1" data-id="element-288">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-289">
                        {step.label}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-mono" data-id="element-290">
                        {step.timestamp}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-291">
                      {step.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Metadata Section */}
        <div data-id="element-292">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4" data-id="element-293">
            Metadati
          </h3>

          <AppCard className="bg-gray-50 dark:bg-dark-subtle" data-id="element-294">
            <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-295">
              <div data-id="element-296">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-297">
                  ID Conversazione
                </p>
                <p className="font-mono text-gray-900 dark:text-gray-100" data-id="element-298">
                  {interaction.id}
                </p>
              </div>
              <div data-id="element-299">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-300">
                  User ID
                </p>
                <p className="font-mono text-gray-900 dark:text-gray-100" data-id="element-301">
                  {interaction.userId}
                </p>
              </div>
              <div data-id="element-302">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-303">
                  Token Usage (stimato)
                </p>
                <p className="font-mono text-gray-900 dark:text-gray-100" data-id="element-304">
                  ~{Math.round(interaction.costEstimate * 100)} tokens
                </p>
              </div>
              <div data-id="element-305">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-306">
                  Latency (stimata)
                </p>
                <p className="font-mono text-gray-900 dark:text-gray-100" data-id="element-307">
                  ~{interaction.traceSteps.length * 1.2}s
                </p>
              </div>
            </div>
          </AppCard>
        </div>
      </div>
    </RightPanel>;
}